import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  List,
  message,
  Modal,
  Select,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../UserContext';
import { ApiTask, BaseComponentProps } from '../../types';
import {
  formatDateWithOptionalTime,
  getAgentTimezone,
  getCrmLink,
} from './utils';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface TaskFormData {
  lead_id: string;
  task_type: string;
  due_date: dayjs.Dayjs;
  details: string;
  header?: string;
}

export const Tasks = ({
  agent,
  apiUrl,
  clientConfig,
}: BaseComponentProps): JSX.Element => {
  const user = useContext(UserContext);
  const [tasks, setTasks] = useState<ApiTask[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const agentTimezone = getAgentTimezone(agent, clientConfig);
  const [postingTaskId, setPostingTaskId] = useState<string | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [useTimesForTasks, setUseTimesForTasks] = useState<boolean>(true);

  const fetchAgentInfo = async () => {
    try {
      if (!user?.accessToken) {
        return;
      }
      const response = await fetch(`${apiUrl}/api/agent/info/${agent.id}`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      });
      if (response.ok) {
        const agentData = await response.json();
        if (agentData.personalization?.use_times_for_tasks !== undefined) {
          setUseTimesForTasks(agentData.personalization.use_times_for_tasks);
        }
      }
    } catch (err) {
      console.error('Failed to fetch agent info:', err);
    }
  };

  const fetchTasks = async (agent: any) => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/agents/${agent.id}/tasks`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.status}`);
      }

      const jsonData = await response.json();
      setTasks(jsonData);
    } catch (err) {
      console.error('An error occurred while fetching tasks:', err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(agent).then().catch(console.log);
    fetchAgentInfo();
  }, [agent]);

  const handleCreateTask = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async (values: TaskFormData) => {
    try {
      if (!user?.accessToken) {
        message.error('No access token available');
        return;
      }
      if (
        !values.lead_id ||
        !values.task_type ||
        !values.due_date ||
        !values.details
      ) {
        message.error('All required fields must be filled');
        return;
      }
      const url = `${apiUrl}/api/tasks/${agent.id}/create`;
      const payload: any = {
        lead_id: values.lead_id,
        task_type: values.task_type,
        due_date: values.due_date.format('YYYY-MM-DDTHH:mm:ss'),
        details: values.details,
      };
      if (values.header) {
        payload.header = values.header;
      }
      payload.use_time = useTimesForTasks;
      console.log('CRM Task payload:', payload);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      if (!response.ok) {
        throw new Error(text);
      }
      message.success('CRM Task created successfully');
      setModalVisible(false);
      form.resetFields();
      await fetchTasks(agent);
    } catch (error) {
      console.error('Error creating CRM task:', error);
      message.error(
        error instanceof Error ? error.message : 'Failed to create CRM task',
      );
    }
  };

  // Sort tasks by due date (closest first)
  const sortedTasks = tasks?.slice().sort((a, b) => {
    if (!a.due_date) {
      return 1;
    }
    if (!b.due_date) {
      return -1;
    }
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });

  return (
    <div>
      <Spin spinning={loading}>
        <Flex style={{ height: '70vh', overflowY: 'auto' }} vertical>
          <Flex
            align="center"
            justify="space-between"
            style={{ marginBottom: '16px' }}
          >
            <Button onClick={() => void fetchTasks(agent)}>Refresh</Button>
            <Button onClick={handleCreateTask} type="primary">
              Create Task
            </Button>
          </Flex>

          {tasks && tasks.length === 0 && (
            <Text style={{ margin: '20px 0', textAlign: 'center' }}>
              No tasks found for this agent.
            </Text>
          )}

          <List
            dataSource={sortedTasks}
            itemLayout="vertical"
            renderItem={task => (
              <List.Item
                extra={
                  <div style={{ textAlign: 'right' }}>
                    {task.is_completed && <Tag color="green">Completed</Tag>}
                    {!task.is_completed &&
                      task.due_date &&
                      new Date(task.due_date) < new Date() && (
                        <Tag color="red">Overdue</Tag>
                      )}
                  </div>
                }
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  margin: '8px 0',
                }}
              >
                <List.Item.Meta
                  description={
                    <div>
                      <Text strong>{task.title}</Text>
                      {task.description && (
                        <div style={{ marginTop: '4px', textAlign: 'left' }}>
                          <Text>{task.description}</Text>
                        </div>
                      )}
                      {task.lead && (
                        <div>
                          <Text>
                            Lead:{' '}
                            {task.lead.source_id &&
                            getCrmLink(clientConfig, task.lead.source_id) ? (
                              <a
                                href={getCrmLink(
                                  clientConfig,
                                  task.lead.source_id,
                                )}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                {task.lead.first_name} {task.lead.last_name}
                              </a>
                            ) : (
                              `${task.lead.first_name} ${task.lead.last_name}`
                            )}
                          </Text>
                        </div>
                      )}
                    </div>
                  }
                  title={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text>
                        Due:{' '}
                        {formatDateWithOptionalTime(
                          task.due_date,
                          agentTimezone,
                        )}
                      </Text>
                    </div>
                  }
                />
                {task.details && (
                  <div style={{ marginTop: '10px' }}>
                    <Text type="secondary">Details:</Text>
                    <div
                      style={{
                        background: '#fff',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '4px',
                      }}
                    >
                      {task.details}
                    </div>
                  </div>
                )}
                <div
                  style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}
                >
                  <div>
                    Task ID: {task.id}{' '}
                    {task.lead_source_id && `• Lead ID: ${task.lead_source_id}`}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Flex>
      </Spin>

      <Modal
        footer={null}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        open={modalVisible}
        title="Create New CRM Task"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Lead ID"
            name="lead_id"
            rules={[{ required: true, message: 'Please enter a lead ID' }]}
          >
            <Input placeholder="Enter lead ID" />
          </Form.Item>

          <Form.Item
            initialValue="Other"
            label="Task Type"
            name="task_type"
            rules={[{ required: true, message: 'Please enter a task type' }]}
          >
            <Input placeholder="Enter task type" />
          </Form.Item>

          <Form.Item
            label="Due Date & Time"
            name="due_date"
            rules={[
              { required: true, message: 'Please select a due date and time' },
            ]}
          >
            <DatePicker
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select due date and time"
              showTime
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="Details"
            name="details"
            rules={[{ required: true, message: 'Please enter details' }]}
          >
            <TextArea placeholder="Enter task details" rows={2} />
          </Form.Item>

          <Form.Item label="Header (optional)" name="header">
            <Input placeholder="Enter header (optional)" />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" justify="end">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button htmlType="submit" type="primary">
                Create
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
